import type { LockInResultDto, LockInStateDto } from '@nsb/core';
import { useState } from 'react';
import { api, errorText, queryClient, useApi } from '../../api.ts';
import { lockInKey } from './useProcess.ts';
import { Button, Confirm, ErrorLine } from '../ui.tsx';
import { dateWords } from '../../days.ts';
import { masterVersionWords } from './masterVersion.ts';

/**
 * The version of the master process tree projects are on, after the page's title (P-12, Jason, 2026-10-09: "There
 * should be a version number for the current master process tree after the title"): the last saved for projects, from
 * the lock-in state the page reads already; nothing until it is known.
 */
export function MasterVersion({ editable }: { editable: boolean }) {
  const { data } = useApi<LockInStateDto>(lockInKey, editable ? '/builder/lock-in' : null);
  if (!data) return null;
  return <span className="ml-3 align-middle font-mono text-[0.9375rem] font-normal tracking-[0.04em] text-ink-3">{masterVersionWords(data.version)}</span>;
}


/**
 * Saving the master process tree for projects (tech-tree spec v3 §8; API domain/lock-in.ts), drawn as a project's own
 * changes are (Jason, 2026-10-07: "make it very clear that when we make adjustments to the master process tree, just
 * like adjustments to the project level process tree, we have to explicitly save it ... this lock the changes in is a
 * teeny little line ... versus the one in the project level shows all the changes and tells you to save it"): how many
 * changes no project has yet, each in its words, and Save the changes. Edits on the page are kept as a draft as they
 * are made; projects get them only when they are saved.
 */
export function LockIn({ editable, onUndo, onDiscarded }: { editable: boolean; onUndo?: () => void; onDiscarded: () => void }) {
  const { data } = useApi<LockInStateDto>(lockInKey, editable ? '/builder/lock-in' : null);
  const [all, setAll] = useState(false);
  const [busy, setBusy] = useState(false);
  const [asking, setAsking] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<LockInResultDto | null>(null);
  if (!data) return null;
  // Discarded: the draft goes back to the tree every project has, its comments and bin kept; the page loads it again.
  const discard = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api('/builder/discard', { body: {} });
      setAsking(false);
      await queryClient.invalidateQueries({ queryKey: lockInKey });
      onDiscarded();
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api<LockInResultDto>('/builder/lock-in', { body: {} });
      setDone(r);
      await queryClient.invalidateQueries({ queryKey: lockInKey });
      // Every project's steps read the tree as saved: whatever reads a project reads it fresh.
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === 'project' });
    } catch (e) {
      setErr(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  const on = data.at ? dateWords(data.at) : null;
  if (!data.changes.length && !data.refused) {
    return (
      <p className="text-sm text-ink-2" aria-live="polite">
        {done ? 'Saved. ' : ''}
        Every project has this tree as it stands{on ? `, saved ${on}` : ''}.
        {done?.left.length ? ` Left with the tree before: ${done.left.join(' ')}` : ''}
      </p>
    );
  }
  const shown = all ? data.changes : data.changes.slice(0, 6);
  const n = `${data.changes.length} ${data.changes.length === 1 ? 'change' : 'changes'}`;
  return (
    <section aria-label="Changes not saved for projects yet" className="flex flex-wrap items-start justify-between gap-x-8 gap-y-3 rounded-[2px] border border-cedar bg-cedar/10 px-4 py-3">
      <div className="min-w-0 max-w-measure">
        <p className="font-medium">{data.changes.length ? `${n} not saved for projects yet` : 'Not saved for projects yet'}</p>
        {shown.length ? (
          <ul className="mt-1 list-disc pl-5 text-[0.9375rem]">
            {shown.map((c, i) => (
              <li key={`${i}:${c}`}>{c}</li>
            ))}
            {!all && data.changes.length > shown.length ? (
              <li className="-ml-5 list-none">
                <button type="button" className="link text-ink-2 hover:text-ink" onClick={() => setAll(true)}>
                  {data.changes.length - shown.length} more
                </button>
              </li>
            ) : null}
          </ul>
        ) : null}
        <p className="meta mt-1">Your edits are kept as a draft as you make them. No project gets them until you save; saved, they reach the steps on every project, except what a project has changed for itself.</p>
        <ErrorLine error={data.refused} />
      </div>
      {editable ? (
        <div className="flex flex-wrap items-center gap-4">
          {asking ? (
            <Confirm
              prompt={`Discard ${data.changes.length ? n : 'the changes'}? The tree goes back to what every project has; comments and the bin stay.`}
              busy={busy}
              onYes={() => void discard()}
              onNo={() => setAsking(false)}
            />
          ) : (
            <>
              {data.refused ? null : (
                <Button busy={busy} onClick={() => void save()}>
                  Save the changes
                </Button>
              )}
              {onUndo ? (
                <Button tone="quiet" onClick={onUndo} title="Ctrl+Z">
                  Undo the last change
                </Button>
              ) : null}
              <Button tone="quiet" onClick={() => setAsking(true)}>
                Discard
              </Button>
            </>
          )}
        </div>
      ) : null}
      <ErrorLine error={err} />
    </section>
  );
}
